import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './components/AuthProvider';
import { getAdAttribution } from './lib/adAttribution';
import {
  getMarketingWebsiteScanStatus,
  sendCAPIEvent,
  startMarketingWebsiteScan,
  submitOnboarding,
  updateMarketingWebsiteScan,
  uploadMarketingWebsiteImage,
} from './lib/api';
import { trackMetaCustomEvent, trackMetaEvent } from './lib/meta';
import posthog from './lib/posthog';
import './PostSignupPage.css';

function MarketingOnboardingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanResult, setScanResult] = useState(null);
  const [editableDescription, setEditableDescription] = useState('');
  const [editableImages, setEditableImages] = useState([]);
  const [savingEdits, setSavingEdits] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    const eventId = trackMetaEvent('ViewContent', {
      content_type: 'marketing_onboarding_website',
      content_name: 'Marketing Onboarding Website Step',
    });
    sendCAPIEvent('ViewContent', eventId, {
      content_type: 'marketing_onboarding_website',
      content_name: 'Marketing Onboarding Website Step',
    }, getAdAttribution()).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;

    (async () => {
      try {
        const result = await getMarketingWebsiteScanStatus();
        if (cancelled) return;
        setScanStatus(result?.status || 'idle');
        setScanResult(result || null);
        if (result?.productWebsiteUrl) {
          setWebsiteUrl(result.productWebsiteUrl);
        }
        setEditableDescription(result?.productDescription || '');
        setEditableImages(result?.productImageUrls || []);
      } catch {
        // Ignore mount-time status failures; user can still submit manually.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  useEffect(() => {
    if (loading || !user || !['queued', 'running'].includes(scanStatus)) return undefined;

    const interval = window.setInterval(async () => {
      try {
        const result = await getMarketingWebsiteScanStatus();
        setScanStatus(result?.status || 'idle');
        setScanResult(result || null);
        setEditableDescription(result?.productDescription || '');
        setEditableImages(result?.productImageUrls || []);
        if (result?.status === 'failed') {
          console.error('Marketing website scan failed:', result?.error);
          setError('We could not scan this website. Please try again.');
        }
      } catch (e) {
        setError('Failed to check website scan status.');
      }
    }, 3000);

    return () => window.clearInterval(interval);
  }, [loading, user, scanStatus]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const trimmedUrl = websiteUrl.trim();
    if (!trimmedUrl) {
      setError('Enter your product website URL.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await submitOnboarding({
        useType: 'marketing',
        productWebsiteUrl: trimmedUrl,
      });
      await startMarketingWebsiteScan({
        productWebsiteUrl: trimmedUrl,
      });
      posthog.capture('marketing onboarding website submitted', {
        has_protocol: trimmedUrl.includes('://'),
      });
      trackMetaCustomEvent('MarketingOnboardingWebsiteSubmitted', {
        has_protocol: trimmedUrl.includes('://'),
        is_rescan: Boolean(scanResult?.productWebsiteUrl),
      });
      setScanStatus('queued');
      setScanResult(null);
      setEditableDescription('');
      setEditableImages([]);
      setSaveMessage('');
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
  };

  const handleRemoveImage = (urlToRemove) => {
    setEditableImages((current) => current.filter((url) => url !== urlToRemove));
    setSaveMessage('');
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingImage(true);
    setError('');
    setSaveMessage('');
    try {
      const result = await uploadMarketingWebsiteImage(file);
      setEditableImages((current) => [...current, result.imageUrl]);
    } catch (e) {
      setError('Failed to upload image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveEdits = async () => {
    setSavingEdits(true);
    setError('');
    setSaveMessage('');
    try {
      const result = await updateMarketingWebsiteScan({
        productDescription: editableDescription,
        productImageUrls: editableImages,
      });
      setScanResult(result);
      setEditableDescription(result?.productDescription || '');
      setEditableImages(result?.productImageUrls || []);
      setSaveMessage('Saved.');
      return result;
    } catch (e) {
      setError('Failed to save changes.');
      return null;
    } finally {
      setSavingEdits(false);
    }
  };

  const handleContinueToCharacter = async () => {
    const result = await handleSaveEdits();
    if (!result) return;
    navigate('/onboarding/marketing/character');
  };

  const hasCompletedScan = scanStatus === 'completed' && scanResult;

  return (
    <div className="post-signup-root">
      <div className="post-signup-card post-signup-card-wide">
        <div className="post-signup-header">
          <h2 className="post-signup-title">What is your product website?</h2>
          <p className="post-signup-subtitle">
            We will use it to scan your site, pull product imagery, and draft a product
            description for the rest of your onboarding flow.
          </p>
        </div>

        {!hasCompletedScan && !(scanStatus === 'queued' || scanStatus === 'running') ? (
          <form className="post-signup-form" onSubmit={handleSubmit}>
            <label className="post-signup-label" htmlFor="product-website-url">
              Product website URL
            </label>
            <input
              id="product-website-url"
              type="url"
              className="post-signup-input"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={(event) => setWebsiteUrl(event.target.value)}
              autoComplete="url"
              autoFocus
              disabled={submitting}
            />
            <p className="post-signup-hint">
              Paste the main site or landing page you want us to analyze.
            </p>
            {error && <div className="post-signup-error">{error}</div>}
            <button type="submit" className="post-signup-submit" disabled={submitting}>
              {submitting ? 'Starting scan…' : 'Continue'}
            </button>
          </form>
        ) : scanStatus === 'queued' || scanStatus === 'running' ? (
          <>
            <div className="post-signup-waiting">
              Scanning your website and extracting product details…
            </div>
            {error && <div className="post-signup-error">{error}</div>}
          </>
        ) : (
          <>
            <div className="post-signup-success">
              Website scanned. We pulled product context from your site and saved it for the next
              step of onboarding.
            </div>

            <div className="post-signup-section">
              <div className="post-signup-section-header">
                <div className="post-signup-section-title">Scan a different website</div>
              </div>
              <form className="post-signup-form" onSubmit={handleSubmit}>
                <label className="post-signup-label" htmlFor="product-website-url-rescan">
                  Product website URL
                </label>
                <input
                  id="product-website-url-rescan"
                  type="url"
                  className="post-signup-input"
                  placeholder="https://example.com"
                  value={websiteUrl}
                  onChange={(event) => setWebsiteUrl(event.target.value)}
                  autoComplete="url"
                  disabled={submitting}
                />
                <p className="post-signup-hint">
                  Paste a different landing page or product site to replace the current scan.
                </p>
                <div className="post-signup-action-row">
                  <button type="submit" className="post-signup-link-btn" disabled={submitting}>
                    {submitting ? 'Starting scan…' : 'Scan new URL'}
                  </button>
                </div>
              </form>
            </div>

            <div className="post-signup-section">
              <div className="post-signup-section-header">
                <div className="post-signup-section-eyebrow">Step 1</div>
                <div className="post-signup-section-title">Review your product context</div>
              </div>
              <div className="post-signup-result">
                <div className="post-signup-result-label">Product description</div>
                <textarea
                  className="post-signup-textarea"
                  value={editableDescription}
                  onChange={(event) => {
                    setEditableDescription(event.target.value);
                    setSaveMessage('');
                  }}
                  rows={6}
                />
              </div>
              <div className="post-signup-result">
                <div className="post-signup-result-label">Product images</div>
                {!!editableImages.length && (
                  <div className="post-signup-image-grid">
                    {editableImages.map((url) => (
                      <div key={url} className="post-signup-image-card">
                        <img src={url} alt="" className="post-signup-image" />
                        <button
                          type="button"
                          className="post-signup-image-remove"
                          onClick={() => handleRemoveImage(url)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {!editableImages.length && (
                  <div className="post-signup-hint">No product images selected yet.</div>
                )}
                <label className="post-signup-upload-btn">
                  {uploadingImage ? 'Uploading…' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="post-signup-file-input"
                    onChange={handleUploadImage}
                    disabled={uploadingImage}
                  />
                </label>
              </div>
              {saveMessage && <div className="post-signup-inline-note">{saveMessage}</div>}
              {error && <div className="post-signup-error">{error}</div>}
            </div>
          </>
        )}
      </div>

      {hasCompletedScan && (
        <div className="post-signup-bottom-bar">
          <div className="post-signup-bottom-bar-inner">
            <div className="post-signup-bottom-meta">
              {saveMessage || 'Review and save your product context before moving on.'}
            </div>
            <button
              type="button"
              className="post-signup-submit"
              onClick={handleContinueToCharacter}
              disabled={savingEdits || uploadingImage}
            >
              {savingEdits ? 'Saving…' : 'Continue to character selection'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingOnboardingPage;
